/**
 * Self-hosted voice pipeline — React hook (flag-gated sibling of
 * {@link useElevenLabsConvAI}).
 *
 * This drives the local (non-ElevenLabs) half-duplex loop:
 *   - reuses the existing `useVoiceSession` lifecycle (POST /voice/session-start
 *     hands us a `sessionId` + a per-session `bearer`);
 *   - opens a SEPARATE raw-binary WebSocket to `/api/voice-ws/audio?bearer=…`
 *     (NOT the proto WebSocketManager — the audio socket carries length-implicit
 *     `[tag][payload]` frames, see internal/voice/orchestrator/types.go);
 *   - wires {@link AudioCapturePipeline} mic chunks → `[0x00]+opus` frames;
 *   - sends snake_case JSON control frames (`[0x01]+json`): start / ptt_down /
 *     ptt_up / interrupt / stop;
 *   - on inbound: tag 0x00 → enqueue playback; tag 0x01 → status / transcript /
 *     agent_done / error.
 *
 * ## Endpoint mode is SERVER-AUTHORITATIVE (design D6)
 *
 * The server declares how turns end via `endpoint_mode` on the `status` control
 * frame:
 *
 *   - `'ptt'`  — push-to-talk. The client owns the endpoint: `pushToTalkDown()`
 *                unmutes + sends `ptt_down`, `pushToTalkUp()` sends `ptt_up`
 *                which the orchestrator turns into `asr.EndUtterance()`.
 *   - `'auto'` — hands-free. The ASR sidecar endpoints semantically (Smart Turn
 *                v3), so the client unmutes on connect, NEVER sends `ptt_up`
 *                (an auto-mode orchestrator ignores it, but sending it is still
 *                a lie about who owns the endpoint), and lets the floor state
 *                gate the mic.
 *
 * The mode is never inferred from a `NEXT_PUBLIC_*` build flag — see the
 * `VoiceEndpointMode` doc-comment for why that fails. It is also **sticky**:
 * the orchestrator only stamps `endpoint_mode` where it has something to say,
 * so an absent field on a later `status` frame means "unchanged", not "revert
 * to ptt".
 *
 * ## Hands-free unmute is gated on LOCAL playback, not just the server (D9)
 *
 * `agent_done` fires when the TTS **stream** closes — NOT when the browser has
 * finished **playing**. {@link AudioCapturePipeline} schedules playback ahead of
 * the clock via its `playbackCursor` for gapless queueing, so seconds of the
 * agent's audio can still be queued when `agent_done` lands. Unmuting on the
 * server signal alone would feed the agent's own voice straight back into ASR,
 * and a semantic endpointer would happily endpoint on it — the model has no way
 * to know it is listening to itself.
 *
 * So the hands-free mic opens only when BOTH hold:
 *   1. the server has released the floor (`agent_done`, or a `status` that is
 *      neither `thinking` nor `agent`), and
 *   2. local playback has genuinely drained (`onPlaybackIdle`, plus our own
 *      in-flight-decode accounting for frames that never produce a source).
 *
 * `echoCancellation: true` is set on the capture constraints but is explicitly
 * NOT relied on here: AEC quality varies wildly by device and is close to
 * useless over Bluetooth, where the far-end reference signal is delayed past
 * the canceller's window.
 *
 * The whole hook is gated behind the runtime `voice_local` feature (delivered
 * by `/api/v1/features` and read via `useFeature('voice_local')`, fail-closed).
 * When off it is an inert no-op (`enabled:false`, all controls are safe stubs)
 * so it can sit alongside the EL path behind a runtime switch without touching
 * the mic or opening a socket.
 *
 * CLAUDE.md FE rules honoured:
 *   - per-request connect watchdog → `'error'` after a timeout (the socket may
 *     never open on a dead link), plus a playback-drain watchdog so a playback
 *     queue that never reports idle cannot wedge the hands-free mic shut
 *     forever. Both timers are per-connection locals inside the effect closure
 *     — never a shared module slot that a second connection could clobber;
 *   - AbortController-style cleanup in the effect (the WS + pipeline are torn
 *     down on unmount / dep change, and a generation token drops late async);
 *   - no floating promises — every promise is awaited or `void`-ed with a
 *     `.catch`;
 *   - control fields are snake_case to match the Go `ControlMessage`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { getConfig } from '@/lib/config';
import { useFeature } from '@/lib/featureFlags';
import { useAuthStore } from '@/stores/authStore';
import { AudioCapturePipeline } from './audioCapture';
import type { VoiceEndpointMode } from './types';
import { useVoiceSession } from './useVoiceSession';

const FRAME_TAG_AUDIO = 0x00;
const FRAME_TAG_CONTROL = 0x01;

/** Floor state mirrored from the server `status` control message. */
export type SelfHostedFloorState = 'idle' | 'user' | 'thinking' | 'agent' | 'error';

/** Connection lifecycle of the audio WebSocket itself. */
export type SelfHostedConnState = 'disabled' | 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/**
 * Client→server control verbs. Mirrors the Go `ControlMessage.Type` values
 * (internal/voice/orchestrator/types.go). snake_case on the wire.
 */
type ClientControlType = 'start' | 'ptt_down' | 'ptt_up' | 'interrupt' | 'stop';

/** Server→client control verbs. */
type ServerControlType = 'status' | 'transcript' | 'agent_done' | 'error';

/**
 * Wire shape of a control frame payload — snake_case, matching the Go struct
 * `ControlMessage`. Only `type` is always present; the rest are conditional.
 */
interface ControlMessage {
  type: ClientControlType | ServerControlType;
  session_id?: string;
  state?: string;
  text?: string;
  final?: boolean;
  /**
   * Server→client only, on `status`: `"ptt"` | `"auto"` (design D6). Absent
   * means "unchanged" — see the sticky-mode note in the file header.
   */
  endpoint_mode?: string;
}

export interface UseSelfHostedVoiceReturn {
  /** False when the feature flag is off — the hook is fully inert. */
  enabled: boolean;
  /** Audio-WebSocket connection state. */
  connState: SelfHostedConnState;
  /** Turn-taking floor state, mirrored from server `status` frames. */
  floorState: SelfHostedFloorState;
  /**
   * How the SERVER says turns end (D6). Defaults to `'ptt'` until the
   * handshake `status` frame lands, so a server that never declares a mode
   * keeps the conservative push-to-talk contract.
   */
  endpointMode: VoiceEndpointMode;
  /**
   * Convenience alias of `endpointMode === 'auto'`. When true the UI must swap
   * the press-and-hold affordance for a live mic-state indicator, and must not
   * call {@link UseSelfHostedVoiceReturn.pushToTalkDown} / `pushToTalkUp`.
   */
  handsFree: boolean;
  /** True while the floor is the agent's (mic muted). */
  isAgentSpeaking: boolean;
  /**
   * True while agent audio is still queued or playing locally. Lags
   * `isAgentSpeaking` — the TTS stream closes before the browser finishes
   * playing (D9) — so the UI should treat `isAgentSpeaking || isPlayingBack`
   * as "the agent has the floor".
   */
  isPlayingBack: boolean;
  /** True while the mic is live (PTT held, or hands-free with the floor open). */
  isListening: boolean;
  /** User-initiated mute. ANDs with the automatic half-duplex gate. */
  isMuted: boolean;
  /** Sets the user mute. Muting closes the mic immediately in both modes. */
  setMuted: (muted: boolean) => void;
  /** Latest (possibly partial) transcript line, for a live caption. */
  liveTranscript: string;
  /** Surfaced error string (connect timeout, server `error` frame, etc.). */
  error: string | null;
  /** Begin holding push-to-talk: unmute mic + tell the server. PTT mode only. */
  pushToTalkDown: () => void;
  /** Release push-to-talk: tell the server end-of-utterance + mute mic. PTT mode only. */
  pushToTalkUp: () => void;
  /** Barge-in: stop playback locally + tell the server to abort synthesis. */
  interrupt: () => void;
}

const CONNECT_TIMEOUT_MS = 30_000;

/**
 * Safety net for the hands-free playback gate. `onPlaybackIdle` fires from
 * `AudioBufferSourceNode.onended`; if a source is scheduled but its `onended`
 * never lands (a suspended/closed AudioContext, a device-switch glitch), the
 * mic would stay shut for the rest of the call with no error shown — the
 * hands-free equivalent of a load state stuck on `'loading'`. The timer is
 * re-armed on every inbound audio frame, so it only expires this long after the
 * LAST frame of an agent turn; playback runs at most a couple of seconds ahead
 * of the stream, so 30s cannot fire mid-utterance.
 */
const PLAYBACK_DRAIN_TIMEOUT_MS = 30_000;

/** Encodes a control message as a `[0x01]+utf8(json)` wire frame. */
function encodeControlFrame(msg: ControlMessage): Uint8Array {
  const json = new TextEncoder().encode(JSON.stringify(msg));
  const out = new Uint8Array(json.length + 1);
  out[0] = FRAME_TAG_CONTROL;
  out.set(json, 1);
  return out;
}

/** Encodes raw Opus bytes as a `[0x00]+opus` wire frame. */
function encodeAudioFrame(opus: Uint8Array): Uint8Array {
  const out = new Uint8Array(opus.length + 1);
  out[0] = FRAME_TAG_AUDIO;
  out.set(opus, 1);
  return out;
}

/**
 * Derives the audio-WS URL from the app's configured WS base. The base ends in
 * `/ws/v2` (the proto socket); we swap the path for the raw audio endpoint and
 * carry the per-session bearer as a query param (the audio socket is off the
 * proto auth handshake, so it authenticates via `?bearer=`).
 */
function buildAudioWsUrl(sessionId: string, userId: string, bearer: string): string {
  const base = getConfig().wsUrl; // e.g. wss://host/ws/v2  or  ws://localhost:8080/ws/v2
  const u = new URL(base);
  u.pathname = '/api/voice-ws/audio';
  // sessionId + userId index the voice-store lookup (mirrors the EL ?userId=
  // pattern); bearer is the security gate. All three ride the query string
  // since the audio socket is off the cookie/JWT auth path.
  u.search =
    `?session=${encodeURIComponent(sessionId)}` +
    `&user=${encodeURIComponent(userId)}` +
    `&bearer=${encodeURIComponent(bearer)}`;
  return u.toString();
}

/**
 * Maps a server `status` frame's `state` string to our floor enum. Unknown
 * values fall back to `'idle'` so a future server state never wedges the UI.
 */
function toFloorState(state: string | undefined): SelfHostedFloorState {
  switch (state) {
    case 'user':
    case 'thinking':
    case 'agent':
    case 'error':
      return state;
    default:
      return 'idle';
  }
}

/**
 * Narrows the wire `endpoint_mode` string. Returns null for absent/unknown
 * values, which the caller treats as "unchanged" rather than a downgrade to
 * `'ptt'` — a future third mode must not silently re-arm the PTT button.
 */
function toEndpointMode(mode: string | undefined): VoiceEndpointMode | null {
  return mode === 'auto' || mode === 'ptt' ? mode : null;
}

/**
 * Drives the self-hosted voice pipeline off the active `useVoiceSession`. Reads
 * `sessionId` + `bearer` from the store (set by `startVoiceSession`) and opens
 * the audio socket once both are present and the call status is `'open'`.
 *
 * Returns inert stubs when the feature flag is off.
 */
export function useSelfHostedVoice(): UseSelfHostedVoiceReturn {
  // Runtime feature gate (fail-closed): replaces the old build-time
  // NEXT_PUBLIC_VOICE_LOCAL_ENABLED const. Read unconditionally at the top of
  // the hook so React's rules-of-hooks hold.
  const voiceLocalEnabled = useFeature('voice_local');
  const sessionId = useVoiceSession((s) => s.sessionId);
  const bearer = useVoiceSession((s) => s.bearer);
  const callStatus = useVoiceSession((s) => s.status);
  const userId = useAuthStore((s) => s.user?.id ?? '');

  const [connState, setConnState] = useState<SelfHostedConnState>(
    voiceLocalEnabled ? 'idle' : 'disabled',
  );
  const [floorState, setFloorState] = useState<SelfHostedFloorState>('idle');
  const [endpointMode, setEndpointMode] = useState<VoiceEndpointMode>('ptt');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlayingBack, setIsPlayingBack] = useState(false);

  // Mutable transport handles live in refs so the control callbacks have a
  // stable identity (no re-subscribe storms) and the effect cleanup can reach
  // them. A generation token drops async work from a superseded session.
  const wsRef = useRef<WebSocket | null>(null);
  const pipelineRef = useRef<AudioCapturePipeline | null>(null);
  const generationRef = useRef(0);
  const floorRef = useRef<SelfHostedFloorState>('idle');
  const endpointModeRef = useRef<VoiceEndpointMode>('ptt');
  /** User-initiated mute — ANDed into the automatic gate in both modes. */
  const userMutedRef = useRef(false);
  /**
   * Hands-free gate, half 1: has the SERVER released the floor? False while the
   * orchestrator reports `thinking` or `agent`. `thinking` counts because the
   * Go floor table rejects a FINAL that arrives in that state (defect S1) —
   * speech captured then is silently thrown away, which reads as the app
   * ignoring the user.
   */
  const floorOpenRef = useRef(true);
  /** Hands-free gate, half 2 (D9): has LOCAL playback genuinely drained? */
  const playbackIdleRef = useRef(true);
  /** True while the hands-free gate says the mic is live (read by `onFrame`). */
  const micLiveRef = useRef(false);
  /** Set once `pipeline.start()` has resolved — before that there is no mic to gate. */
  const captureStartedRef = useRef(false);
  /**
   * Bridges the effect-scoped capture gate / playback-idle helpers out to the
   * stable `useCallback` controls below (which must not close over the effect's
   * `pipeline`). Null between connections, so every call site is optional.
   */
  const applyGateRef = useRef<(() => void) | null>(null);
  const forcePlaybackIdleRef = useRef<(() => void) | null>(null);

  // Keep a ref mirror of the floor so the (stable) onFrame callback can read it
  // without being re-created on every state change.
  floorRef.current = floorState;

  const sendControl = useCallback((msg: ControlMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(encodeControlFrame(msg));
    }
  }, []);

  // --- Lifecycle: open the audio WS when the session is ready --------------
  useEffect(() => {
    if (!voiceLocalEnabled) return undefined;
    // Only open once the REST session-start has handed us a sessionId + bearer
    // and the call is in the open state.
    if (!sessionId || !bearer || callStatus !== 'open') return undefined;

    const generation = ++generationRef.current;
    let disposed = false;

    setConnState('connecting');
    setError(null);
    setFloorState('idle');
    setLiveTranscript('');
    // A fresh connection starts from the conservative default: PTT until the
    // server's handshake `status` frame says otherwise (D6).
    setEndpointMode('ptt');
    endpointModeRef.current = 'ptt';
    setIsPlayingBack(false);
    floorOpenRef.current = true;
    playbackIdleRef.current = true;
    micLiveRef.current = false;
    captureStartedRef.current = false;

    // Per-connection playback-drain accounting. These are effect-locals (not
    // refs) so a superseded connection can never clobber the live one's
    // bookkeeping, and they die with the closure on cleanup.
    let pendingDecodes = 0;
    let sourceScheduled = false;
    let drainTimer: ReturnType<typeof setTimeout> | null = null;

    const clearDrainTimer = (): void => {
      if (drainTimer !== null) {
        clearTimeout(drainTimer);
        drainTimer = null;
      }
    };

    const pipeline = new AudioCapturePipeline({
      onFrame: (opus) => {
        // Half-duplex guard. In hands-free the authoritative answer is the
        // composite gate (server floor AND drained playback AND not user-muted)
        // — the pipeline is muted too, this is belt-and-braces against a chunk
        // that was already in flight when the gate closed.
        if (endpointModeRef.current === 'auto') {
          if (!micLiveRef.current) return;
        } else if (floorRef.current === 'agent') {
          return;
        }
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(encodeAudioFrame(opus));
        }
      },
      onCaptureEnded: (reason) => {
        if (generation !== generationRef.current) return;
        setError(`Microphone capture ended: ${reason}`);
      },
      // D9: the second half of the hands-free unmute gate. Fires when the
      // pipeline's active sources AND pending decodes both hit zero, i.e. the
      // agent's audio has actually finished coming out of the speaker — which
      // is strictly later than the server's `agent_done`.
      onPlaybackIdle: () => {
        if (generation !== generationRef.current || disposed) return;
        markPlaybackIdle();
      },
    });
    pipelineRef.current = pipeline;

    // Per-request watchdog → 'error' if the socket never opens (dead link /
    // server crash). Cleared on open. Keyed to this generation so a superseded
    // attempt can't flip a fresh one to error.
    const connectTimer = setTimeout(() => {
      if (generation !== generationRef.current) return;
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        setConnState('error');
        setError('Voice audio connection timed out after 30s');
        try {
          wsRef.current?.close();
        } catch {
          /* ignore */
        }
      }
    }, CONNECT_TIMEOUT_MS);

    let ws: WebSocket;
    try {
      ws = new WebSocket(buildAudioWsUrl(sessionId, userId, bearer));
    } catch (err) {
      clearTimeout(connectTimer);
      pipeline.dispose();
      if (pipelineRef.current === pipeline) pipelineRef.current = null;
      setConnState('error');
      setError(err instanceof Error ? err.message : 'Failed to open voice audio socket');
      return undefined;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      if (generation !== generationRef.current || disposed) return;
      clearTimeout(connectTimer);
      setConnState('open');
      // Bind this socket to the started voice session. snake_case `session_id`
      // to match the Go ControlMessage; omitted fields stay omitted. The
      // server answers with a `status` frame carrying `endpoint_mode` (D6).
      sendControl({ type: 'start', session_id: sessionId });
      // Start the mic muted, then let the gate decide: in PTT that means it
      // stays shut until a PTT gesture; in hands-free `applyCaptureGate` opens
      // it immediately (no gesture required — that button is the whole point of
      // this task). The mode may already have arrived on an early `status`
      // frame, or may land after `start()` resolves; both orders converge
      // because every mode/floor/playback change re-runs the gate.
      void pipeline
        .start()
        .then(() => {
          if (generation !== generationRef.current || disposed) {
            pipeline.dispose();
            return;
          }
          pipeline.setCaptureMuted(true);
          captureStartedRef.current = true;
          applyCaptureGate();
        })
        .catch((err: unknown) => {
          if (generation !== generationRef.current || disposed) return;
          setError(
            err instanceof Error ? err.message : 'Microphone permission denied or unavailable',
          );
        });
    };

    ws.onmessage = (ev: MessageEvent) => {
      if (generation !== generationRef.current || disposed) return;
      if (!(ev.data instanceof ArrayBuffer)) return;
      const frame = new Uint8Array(ev.data);
      if (frame.length === 0) return;
      const tag = frame[0];
      const payload = frame.subarray(1);

      if (tag === FRAME_TAG_AUDIO) {
        // Agent audio → playback queue. decodeAudioData errors are non-fatal
        // for one frame; log-and-drop rather than killing the call.
        //
        // D9: the moment agent audio exists, hands-free capture closes — and it
        // stays closed until playback actually drains, NOT until the server
        // says the turn is over.
        markPlaybackBusy();
        pendingDecodes++;
        void pipeline
          .enqueuePlayback(payload.slice())
          .then(() => {
            // Resolved without throwing ⇒ a source node was scheduled, so
            // `onPlaybackIdle` will fire when it ends. (Barge-in drops the
            // buffer instead, but `interrupt()` forces the gate open anyway.)
            sourceScheduled = true;
          })
          .catch(() => {
            /* one undecodable frame is non-fatal */
          })
          .finally(() => {
            pendingDecodes--;
            // Every frame failed to decode ⇒ no source was ever created ⇒
            // `onPlaybackIdle` can never fire. Without this the hands-free mic
            // would stay shut for the rest of the call.
            if (pendingDecodes === 0 && !sourceScheduled) {
              if (generation !== generationRef.current || disposed) return;
              markPlaybackIdle();
            }
          });
        return;
      }

      if (tag === FRAME_TAG_CONTROL) {
        let msg: ControlMessage;
        try {
          msg = JSON.parse(new TextDecoder().decode(payload)) as ControlMessage;
        } catch {
          return; // malformed control frame — ignore
        }
        handleServerControl(msg);
      }
    };

    ws.onerror = () => {
      if (generation !== generationRef.current || disposed) return;
      setConnState('error');
      setError('Voice audio socket error');
    };

    ws.onclose = () => {
      if (generation !== generationRef.current || disposed) return;
      clearTimeout(connectTimer);
      clearDrainTimer();
      // Don't clobber an explicit 'error' that was already surfaced.
      setConnState((prev) => (prev === 'error' ? prev : 'closed'));
      micLiveRef.current = false;
      setIsListening(false);
    };

    // --- Capture gate --------------------------------------------------------
    //
    // Single choke point for "should the mic be open right now?". Declared with
    // `function` so it hoists above the callbacks that call it.

    /** Agent audio is in flight: close the hands-free mic and arm the drain watchdog. */
    function markPlaybackBusy(): void {
      playbackIdleRef.current = false;
      setIsPlayingBack(true);
      applyCaptureGate();
      clearDrainTimer();
      drainTimer = setTimeout(() => {
        if (generation !== generationRef.current || disposed) return;
        // See PLAYBACK_DRAIN_TIMEOUT_MS: a scheduled source whose `onended`
        // never landed must not wedge the mic shut for the rest of the call.
        markPlaybackIdle();
      }, PLAYBACK_DRAIN_TIMEOUT_MS);
    }

    /** Playback has drained (or been cleared): release the D9 half of the gate. */
    function markPlaybackIdle(): void {
      clearDrainTimer();
      playbackIdleRef.current = true;
      sourceScheduled = false;
      setIsPlayingBack(false);
      applyCaptureGate();
    }

    function applyCaptureGate(): void {
      if (!captureStartedRef.current) return;
      if (endpointModeRef.current !== 'auto') {
        // PTT mode is unchanged: the mic is opened by the PTT gesture, not by
        // this gate. The only thing the gate owns here is the user mute.
        if (userMutedRef.current) {
          pipeline.setCaptureMuted(true);
          micLiveRef.current = false;
          setIsListening(false);
        }
        return;
      }
      // Hands-free: mic is live iff the server has released the floor AND local
      // playback has drained (D9) AND the user hasn't muted themselves.
      const live = floorOpenRef.current && playbackIdleRef.current && !userMutedRef.current;
      micLiveRef.current = live;
      pipeline.setCaptureMuted(!live);
      setIsListening(live);
    }

    // Expose the gate to the (stable) control callbacks below, which live
    // outside this effect's closure.
    applyGateRef.current = applyCaptureGate;
    forcePlaybackIdleRef.current = markPlaybackIdle;

    // Server-control dispatch — defined inside the effect so it closes over the
    // live `pipeline` + state setters without widening the callback deps.
    function handleServerControl(msg: ControlMessage): void {
      // D6: the endpoint mode rides any server frame that carries it (in
      // practice the handshake `status`). Sticky — an absent field means
      // "unchanged", never "revert to ptt".
      const mode = toEndpointMode(msg.endpoint_mode);
      if (mode !== null && mode !== endpointModeRef.current) {
        endpointModeRef.current = mode;
        setEndpointMode(mode);
        applyCaptureGate();
      }

      switch (msg.type) {
        case 'status': {
          const next = toFloorState(msg.state);
          setFloorState(next);
          floorRef.current = next;
          if (endpointModeRef.current === 'auto') {
            // The server owns the floor in hands-free. `thinking` closes the
            // mic as well as `agent`: a FINAL that lands during Thinking is
            // rejected by the Go floor table and the speech is lost (S1).
            floorOpenRef.current = next !== 'thinking' && next !== 'agent' && next !== 'error';
            applyCaptureGate();
          } else {
            // Half-duplex: mute the mic while the agent holds the floor, unmute
            // otherwise. PTT-down/up still gate listening on top of this.
            pipeline.setCaptureMuted(next === 'agent');
            if (next === 'agent') setIsListening(false);
          }
          break;
        }
        case 'transcript': {
          setLiveTranscript(msg.text ?? '');
          break;
        }
        case 'agent_done': {
          // Agent floor released — clear the caption.
          setLiveTranscript('');
          const next: SelfHostedFloorState = 'idle';
          setFloorState(next);
          floorRef.current = next;
          if (endpointModeRef.current === 'auto') {
            // D9: this is HALF the signal. The TTS stream has closed, but the
            // browser may still have seconds of scheduled audio. Record the
            // server's half and let the gate wait for `onPlaybackIdle`.
            floorOpenRef.current = true;
            applyCaptureGate();
          } else {
            pipeline.setCaptureMuted(true); // idle, awaiting next PTT
          }
          break;
        }
        case 'error': {
          setError(msg.text ?? 'Voice pipeline error');
          setFloorState('error');
          floorRef.current = 'error';
          // Close the hands-free mic — a pipeline in an error state has no
          // consumer for the audio, and an open mic with no endpointer is the
          // worst of both worlds.
          floorOpenRef.current = false;
          applyCaptureGate();
          break;
        }
        default:
          // Unknown / client-only verb echoed back — ignore.
          break;
      }
    }

    return () => {
      disposed = true;
      // Bump generation so any in-flight async (pipeline.start, decode) drops.
      generationRef.current++;
      clearTimeout(connectTimer);
      clearDrainTimer();
      applyGateRef.current = null;
      forcePlaybackIdleRef.current = null;
      micLiveRef.current = false;
      captureStartedRef.current = false;
      // Best-effort graceful stop to the server before closing.
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(encodeControlFrame({ type: 'stop' }));
        } catch {
          /* ignore */
        }
      }
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      if (wsRef.current === ws) wsRef.current = null;
      pipeline.dispose();
      if (pipelineRef.current === pipeline) pipelineRef.current = null;
    };
    // `applyGateRef` / `forcePlaybackIdleRef` are refs (stable identities), so
    // they are deliberately not deps.
  }, [voiceLocalEnabled, sessionId, userId, bearer, callStatus, sendControl]);

  // --- Controls ------------------------------------------------------------

  const pushToTalkDown = useCallback(() => {
    if (!voiceLocalEnabled) return;
    // Hands-free: the server endpoints for us. A PTT gesture here would be a
    // no-op at best and a double-finalize at worst, so refuse it outright.
    if (endpointModeRef.current === 'auto') return;
    if (userMutedRef.current) return;
    // Don't talk over the agent without an explicit interrupt; the UI should
    // surface interrupt() for barge-in instead.
    if (floorRef.current === 'agent') return;
    const pipeline = pipelineRef.current;
    if (pipeline) pipeline.setCaptureMuted(false);
    micLiveRef.current = true;
    setIsListening(true);
    sendControl({ type: 'ptt_down' });
  }, [voiceLocalEnabled, sendControl]);

  const pushToTalkUp = useCallback(() => {
    if (!voiceLocalEnabled) return;
    // NEVER send `ptt_up` in auto mode — the sidecar owns the endpoint (D6).
    if (endpointModeRef.current === 'auto') return;
    sendControl({ type: 'ptt_up' });
    const pipeline = pipelineRef.current;
    if (pipeline) pipeline.setCaptureMuted(true);
    micLiveRef.current = false;
    setIsListening(false);
  }, [voiceLocalEnabled, sendControl]);

  const interrupt = useCallback(() => {
    if (!voiceLocalEnabled) return;
    // Stop hearing the agent immediately (local), then tell the server to abort
    // synthesis. Local-first so barge-in feels instant even before the RTT.
    const pipeline = pipelineRef.current;
    if (pipeline) pipeline.clearPlayback();
    sendControl({ type: 'interrupt' });
    const next: SelfHostedFloorState = 'idle';
    setFloorState(next);
    floorRef.current = next;
    floorOpenRef.current = true;
    // `clearPlayback()` stops the sources WITHOUT firing their `onended`, so
    // `onPlaybackIdle` will never arrive for this turn — force the D9 half of
    // the gate open here or hands-free capture would stay shut forever after
    // every tap-to-interrupt.
    forcePlaybackIdleRef.current?.();
  }, [voiceLocalEnabled, sendControl]);

  const setMuted = useCallback(
    (muted: boolean) => {
      if (!voiceLocalEnabled) return;
      userMutedRef.current = muted;
      setIsMuted(muted);
      const pipeline = pipelineRef.current;
      if (muted && pipeline) {
        // Close immediately; the gate below is for the unmute direction.
        pipeline.setCaptureMuted(true);
        micLiveRef.current = false;
        setIsListening(false);
      }
      applyGateRef.current?.();
    },
    [voiceLocalEnabled],
  );

  return {
    enabled: voiceLocalEnabled,
    connState,
    floorState,
    endpointMode,
    handsFree: endpointMode === 'auto',
    isAgentSpeaking: floorState === 'agent',
    isPlayingBack,
    isListening,
    isMuted,
    setMuted,
    liveTranscript,
    error,
    pushToTalkDown,
    pushToTalkUp,
    interrupt,
  };
}
